import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { FeedsClient } from '@stream-io/feeds-react-sdk';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ArrowLeft, Loader2, Send, Heart, MessageCircle, Trash2, Image as ImageIcon, X,
  GraduationCap, Users, MessageSquare, BarChart3,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PollWidget, CreatePollModal } from './PollComponents';

/**
 * Vista del foro de una clase específica.
 * Reutiliza Stream Feeds (feed_id = `class:{clase_id}`).
 * - Cualquier miembro puede publicar/comentar/reaccionar
 * - Profesor de la clase + admin = moderadores (pueden borrar cualquier post)
 */
const ForoClaseFeed = () => {
  const { claseId } = useParams();
  const navigate = useNavigate();
  const { API, user } = useContext(AuthContext);

  const [info, setInfo] = useState(null);          // info del foro (titulo, prof, etc.)
  const [client, setClient] = useState(null);
  const [feed, setFeed] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null);

  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // 1) Cargar info del foro (con validación de acceso del backend)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/foros-clase/${claseId}/info`, {
          headers: headersRef.current,
        });
        if (!cancelled) setInfo(res.data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 403) setError('No tenés acceso a este foro');
          else if (e.response?.status === 404) setError('Foro no encontrado o deshabilitado');
          else setError('Error cargando el foro');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [API, claseId, user]);

  // 2) Conectar Stream Feeds
  useEffect(() => {
    if (!info) return;
    let mounted = true;
    let fc = null;
    (async () => {
      try {
        const tk = await axios.get(`${API}/stream-feeds/token`, {
          headers: headersRef.current,
        });
        if (!mounted) return;
        const data = tk.data;
        fc = new FeedsClient(data.api_key);
        await fc.connectUser(
          { id: data.user_id, name: data.user_name, image: data.user_image || undefined },
          data.token
        );
        if (!mounted) return;
        setClient(fc);
        setMe({ id: data.user_id, name: data.user_name, image: data.user_image });
      } catch (e) {
        setError('No se pudo conectar al foro');
      }
    })();
    return () => {
      mounted = false;
      try { fc?.disconnectUser?.(); } catch (_) {}
    };
  }, [info, API]);

  // 3) Crear/abrir feed específico de esta clase
  useEffect(() => {
    if (!client || !claseId) return;
    let cancelled = false;
    (async () => {
      try {
        const feedSlug = `class_${claseId}`;
        const feedFqId = `user:${feedSlug}`;
        const f = client.feed('user', feedSlug);
        await f.getOrCreate({ watch: true });
        if (cancelled) return;
        setFeed(f);

        // Cargar últimas activities del feed específico
        const queryRes = await client.queryActivities({
          filter: { feeds: [feedFqId] },
          sort: [{ field: 'created_at', direction: -1 }],
          limit: 30,
        });
        if (cancelled) return;
        setActivities(queryRes?.activities || []);
        setLoading(false);

        // Suscribirse a cambios del feed para refrescar en tiempo real
        const unsub = f.state.subscribe((s) => {
          const incoming = s.activities || [];
          setActivities(prev => {
            const ids = new Set(prev.map(a => a.id));
            const news = incoming.filter(a => !ids.has(a.id));
            if (news.length === 0) return prev;
            return [...news, ...prev];
          });
        });
        return () => unsub?.();
      } catch (err) {
        console.error('Error abriendo feed clase:', err);
        setLoading(false);
        setError('No se pudo abrir el foro');
      }
    })();
    return () => { cancelled = true; };
  }, [client, claseId]);

  // Refresca las activities desde Stream Feeds (para usar después de crear un poll, etc.)
  const reloadActivities = useCallback(async () => {
    if (!client || !claseId) return;
    try {
      const feedFqId = `user:class_${claseId}`;
      const queryRes = await client.queryActivities({
        filter: { feeds: [feedFqId] },
        sort: [{ field: 'created_at', direction: -1 }],
        limit: 30,
      });
      setActivities(queryRes?.activities || []);
    } catch (_) {}
  }, [client, claseId]);

  // Publicar en el foro server-side. Los foros usan feeds tipo `user:class_<id>`
  // cuyo "owner" es un user sintético, por eso los users normales no pueden
  // publicar directamente desde el SDK (Stream devuelve 403). El backend
  // realiza la publicación en nombre del usuario real.
  const handleCreatePost = useCallback(async (text, imageFile) => {
    if (!feed) throw new Error('Foro no listo');

    // Subir la imagen primero (si hay) usando el SDK del cliente
    const attachments = [];
    if (imageFile && client) {
      try {
        const upload = await client.uploadImage({ file: imageFile });
        // El SDK Feeds devuelve { file: <url> }, no { url }. Probamos varios shapes.
        const url = upload?.file || upload?.url || upload?.data?.file;
        if (url) {
          attachments.push({
            type: 'image',
            asset_url: url,
            image_url: url,
          });
        } else {
          console.warn('uploadImage sin URL en payload:', upload);
          toast.error('No se pudo subir la imagen (sin URL)');
        }
      } catch (e) {
        console.error('uploadImage error:', e);
        toast.error('Error subiendo la imagen');
      }
    }

    // Publicar via backend server-side
    let activityId = null;
    try {
      const r = await axios.post(
        `${API}/foros-clase/${claseId}/post`,
        { text: text || '', attachments },
        { headers: headersRef.current }
      );
      activityId = r?.data?.activity_id;
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      throw new Error(detail || 'No se pudo publicar');
    }

    toast.success('Publicado en el foro');

    // Reintento del refresh: primero inmediato, después con 800ms de delay
    // para cubrir la consistencia eventual del feed (~700ms).
    const refresh = async () => {
      try {
        const feedFqId = `user:class_${claseId}`;
        const queryRes = await client.queryActivities({
          filter: { feeds: [feedFqId] },
          sort: [{ field: 'created_at', direction: -1 }],
          limit: 30,
        });
        setActivities(queryRes?.activities || []);
      } catch (_) {}
    };
    await refresh();
    setTimeout(() => { refresh(); }, 800);

    // Notificación push best-effort
    try {
      await axios.post(
        `${API}/foros-clase/${claseId}/notify-new-post`,
        {
          text: text || '',
          activity_id: activityId,
          has_image: attachments.length > 0,
        },
        { headers: headersRef.current }
      );
    } catch (notifErr) {
      console.warn('No se pudo enviar push a miembros del foro:', notifErr?.message);
    }
  }, [feed, client, API, claseId]);

  // Borrar post (solo moderador)
  const handleDelete = useCallback(async (activity) => {
    if (!info?.puede_moderar) return;
    if (!window.confirm('¿Borrar este post?')) return;
    try {
      await axios.post(
        `${API}/stream-feeds/delete-activity`,
        { activity_id: activity.id, feed_id: `user:class_${claseId}` },
        { headers: headersRef.current }
      );
      setActivities(prev => prev.filter(a => a.id !== activity.id));
      toast.success('Post borrado');
    } catch (e) {
      toast.error('Error al borrar');
    }
  }, [API, claseId, info?.puede_moderar]);

  // Reaccionar (like)
  const handleLike = useCallback(async (activity) => {
    if (!client) return;
    try {
      const myReaction = (activity.own_reactions?.heart || []).length > 0;
      if (myReaction) {
        await client.deleteActivityReaction({ activity_id: activity.id, type: 'heart' });
      } else {
        await client.addActivityReaction({ activity_id: activity.id, type: 'heart' });
      }
    } catch (e) {
      // silent
    }
  }, [client]);

  // ----------------------------- UI -----------------------------
  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/app/foros-clase')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver
        </Button>
        <Card><CardContent className="p-8 text-center text-red-600">{error}</CardContent></Card>
      </div>
    );
  }

  if (loading || !info || !client) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <p className="text-sm text-gray-500 mt-3">Abriendo foro...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-4" data-testid={`foro-clase-${claseId}`}>
      {/* Header */}
      <Button
        variant="outline"
        onClick={() => navigate('/app/foros-clase')}
        size="sm"
        className="mb-3"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> Mis foros
      </Button>

      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-600 rounded-xl p-5 text-white shadow-lg mb-5">
        <div className="flex items-start gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <MessageSquare className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold">{info.clase_titulo}</h1>
            <div className="flex items-center gap-3 flex-wrap mt-2 text-xs text-white/90">
              {info.profesor_nombre && (
                <span className="flex items-center gap-1">
                  <GraduationCap className="h-3 w-3" /> {info.profesor_nombre}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" /> {info.estudiantes_count} miembros
              </span>
              {info.puede_moderar && (
                <Badge className="bg-emerald-500/30 text-white border-0 text-[10px]">
                  Moderador
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Composer */}
      <ForoComposer
        onSubmit={handleCreatePost}
        userName={me?.name}
        userImage={me?.image}
        API={API}
        claseId={claseId}
        canCreatePoll={!!info?.puede_moderar}
        onPollCreated={reloadActivities}
      />

      {/* Lista de posts */}
      <div className="mt-5 space-y-4">
        {activities.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <MessageCircle className="h-10 w-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm">Aún no hay posts en este foro.</p>
              <p className="text-xs text-gray-400 mt-1">Sé el primero en compartir algo.</p>
            </CardContent>
          </Card>
        ) : (
          activities.map(act => (
            <PostItem
              key={act.id}
              activity={act}
              canModerate={info.puede_moderar}
              client={client}
              onLike={() => handleLike(act)}
              onDelete={() => handleDelete(act)}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Composer simple (texto + imagen + @menciones + encuesta) ───
const ForoComposer = ({ onSubmit, userName, userImage, API, claseId, canCreatePoll, onPollCreated }) => {
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const textareaRef = React.useRef(null);

  // Autocomplete de @menciones (mismo patrón que Red Social)
  const [allUsers, setAllUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null); // {start, query} o null
  const [mentionIndex, setMentionIndex] = useState(0);

  // Cargar lista de usuarios una vez
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/stream-chat/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAllUsers(data.users || []);
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, [API]);

  // Detecta si el cursor está escribiendo una @mención
  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart || 0;
    const before = val.slice(0, caret);
    const match = before.match(/@([\w.]*)$/);
    if (match) {
      setMentionQuery({ start: caret - match[0].length, query: match[1].toLowerCase() });
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredUsers = mentionQuery
    ? allUsers
        .filter((u) => {
          const q = mentionQuery.query;
          if (!q) return true;
          return (
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
          );
        })
        .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
        )
        .slice(0, 50)
    : [];

  const pickMention = (u) => {
    if (!mentionQuery) return;
    const handle = (u.name || u.email || 'usuario').replace(/\s+/g, '_');
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    const newText = `${before}@${handle} ${after}`;
    setText(newText);
    setMentionQuery(null);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = before.length + handle.length + 2;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 10);
  };

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB');
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const handleSubmit = async () => {
    if (!text.trim() && !imageFile) return;
    setSubmitting(true);
    try {
      await onSubmit(text.trim(), imageFile);
      setText('');
      clearImage();
    } catch (e) {
      toast.error('Error al publicar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex gap-3">
          {userImage ? (
            <img src={userImage} alt={userName || 'avatar'} className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="h-10 w-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold flex-shrink-0">
              {(userName || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              placeholder="¿Qué querés compartir con la clase? Usá @ para mencionar"
              className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              rows={3}
              maxLength={2000}
              data-testid="foro-composer-input"
            />

            {/* Dropdown @menciones */}
            {mentionQuery && filteredUsers.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-20" data-testid="foro-mention-dropdown">
                <div className="max-h-72 overflow-y-auto">
                  {filteredUsers.map((u, idx) => (
                    <button
                      type="button"
                      key={u.id || u.email || idx}
                      onClick={() => pickMention(u)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                        idx === mentionIndex ? 'bg-indigo-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {u.foto_perfil ? (
                        <img src={u.foto_perfil} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white text-xs font-bold flex items-center justify-center">
                          {(u.name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {imagePreview && (
              <div className="relative mt-2 inline-block">
                <img src={imagePreview} className="max-h-40 rounded-lg" alt="preview" />
                <button
                  onClick={clearImage}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            <div className="flex justify-between items-center mt-3">
              <div className="flex items-center gap-3">
                <label className="cursor-pointer text-gray-500 hover:text-indigo-600 text-sm flex items-center gap-1">
                  <ImageIcon className="h-4 w-4" />
                  <span>Imagen</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                </label>
                {canCreatePoll && (
                  <button
                    type="button"
                    onClick={() => setShowPollModal(true)}
                    className="text-gray-500 hover:text-indigo-600 text-sm flex items-center gap-1"
                    data-testid="foro-create-poll-btn"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Encuesta</span>
                  </button>
                )}
              </div>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting || (!text.trim() && !imageFile)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="foro-composer-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Publicando</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1.5" />Publicar</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      {canCreatePoll && (
        <CreatePollModal
          isOpen={showPollModal}
          onClose={() => setShowPollModal(false)}
          onCreated={() => onPollCreated?.()}
          API={API}
          endpoint={`/foros-clase/${claseId}/polls`}
          accent="indigo"
          title="Crear Encuesta en el Foro"
        />
      )}
    </Card>
  );
};
const renderRichText = (text) => {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/\S+|www\.\S+|@[\w.]+|#[\wáéíóúñÁÉÍÓÚÑ]+)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('http://') || part.startsWith('https://') || part.startsWith('www.')) {
      const trailMatch = part.match(/^([\s\S]*?)([.,!?;:)\]]+)$/);
      const cleanUrl = trailMatch ? trailMatch[1] : part;
      const trail = trailMatch ? trailMatch[2] : '';
      const href = part.startsWith('www.') ? `https://${cleanUrl}` : cleanUrl;
      const display = cleanUrl.length > 60 ? cleanUrl.slice(0, 57) + '…' : cleanUrl;
      return (
        <React.Fragment key={i}>
          <a href={href} target="_blank" rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-indigo-600 hover:underline break-all font-medium">
            {display}
          </a>{trail}
        </React.Fragment>
      );
    }
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-indigo-600 font-semibold bg-indigo-50 px-1 rounded">
          {part}
        </span>
      );
    }
    if (part.startsWith('#')) {
      return (
        <span key={i} className="text-purple-600 font-semibold">
          {part}
        </span>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// ─── Item de post (con hilo de comentarios) ───
const PostItem = ({ activity, canModerate, onLike, onDelete, client }) => {
  const text = activity.text || '';
  const userName = activity.user?.name || 'Anónimo';
  const userImage = activity.user?.image;
  const createdAt = activity.created_at ? new Date(activity.created_at) : new Date();
  const reactions = activity.reaction_groups?.heart || activity.reaction_counts?.heart || 0;
  const heartCount = typeof reactions === 'object' ? (reactions.count || 0) : reactions;
  const myHeart = (activity.own_reactions?.heart || []).length > 0;
  const imgAtt = (activity.attachments || []).find(a => a.type === 'image');

  // Comentarios (hilo)
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentDelta, setCommentDelta] = useState(0);
  const commentCount = (activity.comment_count || 0) + commentDelta;

  const loadComments = useCallback(async () => {
    if (!client || !activity?.id) return;
    setLoadingComments(true);
    try {
      const res = await client.getComments({ object_id: activity.id, object_type: 'activity', limit: 50 });
      setComments(res?.comments || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [client, activity?.id]);

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !client || !activity?.id) return;
    setSubmittingComment(true);
    try {
      await client.addComment({
        comment: commentText.trim(),
        object_id: activity.id,
        object_type: 'activity',
      });
      setCommentText('');
      setCommentDelta((n) => n + 1);
      await loadComments();
    } catch (err) {
      console.error('Error comment:', err);
      toast.error('No se pudo comentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!client || !commentId) return;
    if (!window.confirm('¿Borrar este comentario?')) return;
    try {
      await client.deleteComment({ id: commentId });
      setCommentDelta((n) => n - 1);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (err) {
      toast.error('No se pudo borrar');
    }
  };

  const formatTime = (d) => {
    const diff = (new Date() - d) / 60000;
    if (diff < 1) return 'Ahora';
    if (diff < 60) return `Hace ${Math.floor(diff)} min`;
    if (diff < 60 * 24) return `Hace ${Math.floor(diff / 60)} h`;
    return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Card>
      <CardContent className="p-0 overflow-hidden">
        <div className="p-4">
          <div className="flex gap-3">
            {userImage ? (
              <img src={userImage} alt={userName || ''} className="h-10 w-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold flex-shrink-0">
                {userName.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm text-gray-800">{userName}</div>
                  <div className="text-[11px] text-gray-500">{formatTime(createdAt)}</div>
                </div>
                {canModerate && (
                  <button
                    onClick={onDelete}
                    className="text-gray-400 hover:text-red-600 p-1"
                    title="Borrar post"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {text && (
                <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap break-words">
                  {renderRichText(text)}
                </p>
              )}
              {imgAtt?.image_url && (
                <img
                  src={imgAtt.image_url}
                  alt="post"
                  className="mt-2 rounded-lg max-h-80 w-auto"
                />
              )}
              {activity?.type === 'poll' && activity?.custom?.poll_id && (
                <PollWidget
                  pollId={activity.custom.poll_id}
                  initialQuestion={activity.custom.question}
                  initialOptions={activity.custom.options || []}
                  accent="indigo"
                />
              )}
              <div className="flex items-center gap-4 mt-3 text-sm">
                <button
                  onClick={onLike}
                  className={`flex items-center gap-1 transition ${
                    myHeart ? 'text-rose-600' : 'text-gray-500 hover:text-rose-600'
                  }`}
                >
                  <Heart className={`h-4 w-4 ${myHeart ? 'fill-rose-600' : ''}`} />
                  <span className="text-xs">{heartCount}</span>
                </button>
                <button
                  onClick={() => setShowComments((v) => !v)}
                  className="flex items-center gap-1 text-gray-500 hover:text-indigo-600 transition"
                  data-testid={`foro-comentarios-toggle-${activity.id}`}
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="text-xs">
                    {commentCount > 0 ? commentCount : ''} {commentCount === 1 ? 'Comentario' : 'Comentarios'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sección de comentarios (hilo) */}
        {showComments && (
          <div className="bg-gray-50 border-t border-gray-100 p-4">
            {loadingComments ? (
              <div className="flex justify-center py-2">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {comments.length === 0 && (
                  <p className="text-sm text-gray-500 italic text-center py-2">
                    Sin comentarios todavía. Sé el primero en comentar.
                  </p>
                )}
                {comments.map((c) => {
                  const isMine = c.user?.id && client?.userID && c.user.id === client.userID;
                  const canDeleteComment = canModerate || isMine;
                  return (
                    <div key={c.id} className="bg-white rounded-lg px-3 py-2 text-sm shadow-sm">
                      <div className="flex items-start justify-between gap-2 mb-0.5">
                        <div className="flex items-center gap-2">
                          {c.user?.image ? (
                            <img src={c.user.image} alt={c.user.name} className="w-5 h-5 rounded-full object-cover" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center">
                              {(c.user?.name || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="font-semibold text-gray-800 text-xs">{c.user?.name || 'Usuario'}</span>
                          <span className="text-[10px] text-gray-400">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : ''}
                          </span>
                        </div>
                        {canDeleteComment && (
                          <button
                            onClick={() => handleDeleteComment(c.id)}
                            className="text-gray-300 hover:text-red-500 p-0.5"
                            title="Borrar comentario"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <p className="text-gray-700 break-words whitespace-pre-wrap">
                        {renderRichText(c.comment || c.text)}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
            <form onSubmit={submitComment} className="flex items-center gap-2 mt-3">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Escribí un comentario..."
                className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                disabled={submittingComment}
                data-testid={`foro-comentario-input-${activity.id}`}
              />
              <button
                type="submit"
                disabled={submittingComment || !commentText.trim()}
                className="px-3 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50 hover:bg-indigo-700 transition-colors"
                data-testid={`foro-comentario-submit-${activity.id}`}
              >
                {submittingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ForoClaseFeed;
